"use client";
import { brandAssets } from "constants/brand";
import { use, useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslation } from "hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "appHooks";
import { fetchClient } from "features/client/client.actions";
import { setLocale, setSlug } from "features/config/config.reducer";
import { clientSelector } from "features/client/client.selectors";
import { configSelector } from "features/config/config.selectors";
import { loginUser, sendOtp } from "features/auth/auth.actions";
import Loader from "shared/Loader";
import AuthHeader from "components/AuthHeader";
import { authSelector } from "features/auth/auth.selectors";
import { apiHeadersSelector } from "features/selectors";
import { resetState } from "features/auth/auth.reducer";
import { toast } from "react-toastify";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import SkeletonThemeProps from "constants/SkeletonTheme";
import "react-loading-skeleton/dist/skeleton.css";
import "./custom-style.css";

const LoginSkeleton = ({
  locale,
  client,
  t,
  onChangeLang,
}: {
  locale: string;
  client: any;
  t: (key: string) => string;
  onChangeLang: () => void;
}) => (
  <SkeletonTheme
    {...SkeletonThemeProps(locale).UnAuth}
    direction={locale === "ar" ? "rtl" : "ltr"}
  >
    <AuthHeader
      lightLogo={client.light_logo || client.logo}
      darkLogo={client.logo || client.light_logo}
      alt={t("login.logoAlt")}
      langLabel={t("lang")}
      onChangeLang={onChangeLang}
    />
    <div className="auth-wrapper">
      <div className="auth-left-block">
        <div className="auth-logo">
          <Skeleton width={"100%"} height={"100%"} />
        </div>
      </div>
      <div className="auth-right-block">
        <div className="auth-header">
          <h2>
            <Skeleton width={200} />
          </h2>
        </div>
        <div className="auth-form-block">
          <div className="form-title">
            <Skeleton width={150} />
          </div>
          <div className="auth-text-block">
            <Skeleton width={100} />
            <Skeleton height={40} />
          </div>
          <div className="auth-text-block">
            <Skeleton width={100} />
            <Skeleton height={40} />
          </div>
          <div className="auth-text-block auth-text-block__action">
            <Skeleton height={45} />
            <Skeleton width={150} />
          </div>
        </div>
        <Skeleton width={80} />
      </div>
    </div>
  </SkeletonTheme>
);

const Login = ({
  params,
}: {
  params: Promise<{ locale: string }>;
}) => {
  const { locale } = use(params);
  const t = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { data: auth } = useAppSelector(authSelector);
  const { data: client } = useAppSelector(clientSelector);
  const apiHeaders = useAppSelector(apiHeadersSelector);
  const { slug } = useAppSelector(configSelector);
  const [isLoading, setIsLoading] = useState(false);
  const [isSSOLoading, setIsSSOLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const isFetching = useRef(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // Static client slug
  const clientSlug = "greenriyadh";
  // const clientSlug = "Mr.DoyleSchneider";

  // Only reset state if user is not logged in
  useEffect(() => {
    if (!auth.token) {
      dispatch(resetState());
    }
  }, [dispatch, auth.token]);

  const fetchClientData = async (langCode?: string, force?: boolean) => {
    if ((isFetched && !force) || isFetching.current) return;
    isFetching.current = true;
    try {
      await dispatch(
        fetchClient({
          clientSlug: clientSlug,
          langCode: langCode || locale,
          apiHeaders,
        })
      ).unwrap();
      setIsFetched(true);
    } catch (error: any) {
      setError(error.message);
    } finally {
      isFetching.current = false;
    }
  };

  useEffect(() => {
    dispatch(setLocale(locale));
    dispatch(setSlug(clientSlug));
  }, [locale, dispatch, clientSlug]);

  // Redirect logged-in users to intro page
  useEffect(() => {
    if (auth.token) {
      router.push(`/${locale}/`);
      return;
    }
  }, [auth.token, locale, router]);

  // Fetch client data if not already fetched
  useEffect(() => {
    if (!isFetched && !auth.token) {
      fetchClientData(); // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [isFetched, auth.token]);

  const handleLogin = async () => {
    if (email === "" || password === "") {
      setError(t("login.enterRequiredFields"));
    } else {
      setIsLoading(true);
      try {
        const response = await dispatch(
          loginUser({
            email,
            password,
            client_id: client.id,
            apiHeaders,
          })
        ).unwrap();

        // Check if token is returned (OTP skipped)
        if (response && response.token) {
          router.push(`/${locale}/`);
        } else {
          // Determine available OTP methods
          const usePref =
            response.is_prioritized_user_preference_enabled === 1 ||
            response.is_prioritized_user_preference_enabled === true;
          const emailEnabled = usePref
            ? response.email_otp_enabled === 1
            : client.email_otp_enabled === 1;
          const smsEnabled = usePref
            ? response.sms_otp_enabled === 1
            : client.sms_otp_enabled === 1;

          if (emailEnabled && smsEnabled) {
            // Both methods available — let user choose
            router.push(`/${locale}/${clientSlug}/send-otp`);
          } else {
            // Single method (or default to email) — auto-send
            const otpType = smsEnabled ? "sms" : "email";
            const otpValue =
              otpType === "sms" ? response.phone : response.email;

            if (typeof window !== "undefined") {
              localStorage.setItem("otp-value", otpValue);
              localStorage.setItem("otp-type", otpType);
            }

            const md5 = await dispatch(
              sendOtp({
                [otpType === "sms" ? "phone" : "email"]:
                  otpType === "sms" ? response.phone : response.email,
                otp_type: otpType,
                login_hash: response.login,
                apiHeaders,
              })
            ).unwrap();

            if (typeof window !== "undefined") {
              localStorage.setItem("md5", md5);
            }

            router.push(`/${locale}/${clientSlug}/check-otp`);
          }
        }
      } catch (error: any) {
        toast.error(error?.message);
        setError(error?.message);
        setIsLoading(false);
      }
    }
  };

  const handleSSOLogin = async () => {
    setIsSSOLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.backEndPoint}/client/v1/auth/mdfs/${clientSlug}?locale=${locale}`,
        {
          method: "GET",
          headers: { "Accept": "application/json" },
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Failed to fetch SSO URL");
      }

      const data = await response.json();

      if (data?.fullUrl) {
        window.location.href = data.fullUrl;
      } else {
        throw new Error("SSO URL not provided by the server.");
      }
    } catch (error: any) {
      console.error("SSO login error:", error);
      toast.error(error?.message || "SSO login failed");
      setError(error?.message || "SSO login failed");
      setIsSSOLoading(false);
    }
  };

  const changeLocaleAndFetchClient = async (langCode: string) => {
    try {
      dispatch(setLocale(langCode));
      // Reset fetch status to force a reload with the new language
      await fetchClientData(langCode, true);
      router.replace(`/${langCode}/login`);
    } catch (error: any) {
      setError(error.message);
    }
  };

  if (!isFetched) return <LoginSkeleton locale={locale} client={client} t={t} onChangeLang={() => changeLocaleAndFetchClient(t(locale))} />;

  const hasUsernamePassword = client?.login_options?.username_password === 1;
  const hasSSOEnabled = client?.login_options?.sso_login_enabled === 1;
  const showBothOptions = hasUsernamePassword && hasSSOEnabled;

  return (
    <>
      {isLoading && <Loader showOverlay />}
      <AuthHeader
        lightLogo={client.light_logo || client.logo}
        darkLogo={client.logo || client.light_logo}
        alt={t("login.logoAlt")}
        langLabel={t("lang")}
        onChangeLang={() => changeLocaleAndFetchClient(t(locale))}
      />
      <div className="auth-wrapper">
        <div
          className="auth-left-block auth-bg"
          style={{ backgroundImage: `url(${client.cover})` }}
        >
          <div className="auth-logo">
            <img
              src={client.logo || brandAssets.darkLogo}
              alt={t("login.logoAlt")}
              style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
            />
          </div>
        </div>
        <div className="auth-right-block">
          <div className="auth-header">
            <h2>{client.title}</h2>
          </div>
          <div className="auth-form-block">
            <div className="form-title">{t("login.signIn")}</div>

            {/* Username and Password Fields */}
            {hasUsernamePassword && (
              <>
                <div className="auth-text-block">
                  <label htmlFor="email" className="auth-title">
                    {t("login.email")} <span>*</span>
                  </label>
                  <input
                    type="text"
                    name="email"
                    value={email}
                    className="auth-textbox"
                    required
                    onChange={(e) => {
                      setError("");
                      setEmail(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleLogin();
                    }}
                    placeholder={t("login.emailPlaceholder")}
                    id="email"
                  />
                </div>
                <div className="auth-text-block">
                  <label htmlFor="password" className="auth-title">
                    {t("login.password")} <span>*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={password}
                    className="auth-textbox"
                    required
                    onChange={(e) => {
                      setError("");
                      setPassword(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleLogin();
                    }}
                    placeholder={t("login.passwordPlaceholder")}
                    id="password"
                  />
                </div>
              </>
            )}

            {/* Actions Container */}
            <div className="auth-text-block auth-text-block__action">
              {/* Username/Password Login Button */}
              {hasUsernamePassword && (
                <button
                  type="submit"
                  className="auth-submit"
                  onClick={handleLogin}
                >
                  {t("login.signInButton")}
                </button>
              )}

              {/* Error Message Display */}
              {error && (
                <div className="error-message">
                  <p>{error}</p>
                </div>
              )}

              {/* "Or" Divider */}
              {showBothOptions && (
                <div className="sso-divider">
                  <span className="sso-divider-text">
                    {t("login.orContinueWith")}
                  </span>
                </div>
              )}

              {/* SSO Login Button */}
              {hasSSOEnabled && (
                <div className="sso-login-section">
                  <button
                    type="button"
                    className="sso-login-button"
                    onClick={handleSSOLogin}
                    disabled={isSSOLoading}
                  >
                    {isSSOLoading && (
                      <span className="sso-spinner">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="animate-spin icon icon-tabler icons-tabler-outline icon-tabler-refresh"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
                          <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
                        </svg>
                      </span>
                    )}
                    {isSSOLoading
                      ? t("login.connecting")
                      : t("login.loginWithSso")}
                  </button>
                </div>
              )}

              {/* Forgot Password Link */}
              {hasUsernamePassword && (
                <Link
                  href={`/${locale}/${clientSlug}/forget-password`}
                  className="auth-forgotpassword"
                >
                  {t("login.forgotPassword")}
                </Link>
              )}

            </div>

            {/* No Login Options Available Message */}
            {!hasUsernamePassword && !hasSSOEnabled && (
              <div className="error-message">
                <p></p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;

